import { inject, Injectable } from '@angular/core';
import { scalePoint } from '../../../shared/misc-utilities/point-utils';
import { ViewBox } from '../../../team-apis/content/view-box';
import { Point2D } from '../../../team-apis/otterdam/answers/shape-typings';
import { MouseCoordinateService } from '../mouse-coordinate.service';
import { AbstractSensorContentStore2D } from '../state/abstract-sensor-content-store-2d';

@Injectable()
export class ViewBoxBuilderService {
  private store = inject(AbstractSensorContentStore2D);
  private mouseCoordinateService = inject(MouseCoordinateService);

  async buildFromBoundingPoints(
    boundingPoints: [Point2D, Point2D],
  ): Promise<ViewBox> {
    const { pixelSize } = this.store.state();
    const { xMin, xMax, yMin, yMax } = this.getBoxCoordinates(boundingPoints);

    // Add one, since e.g. a 2x2 box actually covers 3 pixels.
    const boxWidth = xMax - xMin + pixelSize;
    const boxHeight = yMax - yMin + pixelSize;

    const [svgWidth, svgHeight] =
      await this.mouseCoordinateService.getSvgElementSize();
    const svgAspectRatio = svgWidth / svgHeight;
    const boxAspectRatio = boxWidth / boxHeight;

    // We want to respect the aspect ratio of the SVG element. This ratio will
    // be used fit and center the viewBox. It follows the same logic as
    // preserveAspectRatio="xMidYMid meet"
    const ratio =
      boxAspectRatio < svgAspectRatio
        ? boxHeight / svgHeight
        : boxWidth / svgWidth;

    const [newViewBoxWidth, newViewBoxHeight] = scalePoint(
      [svgWidth, svgHeight],
      ratio,
    );

    return [
      xMin + (boxWidth - newViewBoxWidth) / 2,
      yMin + (boxHeight - newViewBoxHeight) / 2,
      newViewBoxWidth,
      newViewBoxHeight,
    ];
  }

  private getBoxCoordinates(corners: [Point2D, Point2D]) {
    const xMin = Math.min(corners[0][0], corners[1][0]);
    const xMax = Math.max(corners[0][0], corners[1][0]);
    const yMin = Math.min(corners[0][1], corners[1][1]);
    const yMax = Math.max(corners[0][1], corners[1][1]);

    return { xMin, xMax, yMin, yMax };
  }
}
